import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
import { useState, useEffect, useRef, useCallback } from 'react';
import { AppUIActions } from 'app/store/AppUI/AppUI.slice';
import { selectPreviewPage, selectReferencePage } from 'app/store/AppUI/AppUI.selectors';
import { SxProps } from '@mui/material';

interface IOptions {
  activeLinkClassName?: string;
  disableLinkPreview?: boolean;
  previewPageSx?: SxProps;
}

export default function useHTMLLinkClickHandler(options: IOptions = {}) {
  const {
    activeLinkClassName = 'highlighted-link',
    disableLinkPreview = false,
    previewPageSx = {}
  } = options;
  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const previewPage = useAppSelector(selectPreviewPage);
  const referencePage = useAppSelector(selectReferencePage);

  // We use a ref to get a stable reference to the container element
  // where the HTML will be rendered.
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeLinkIndex, setActiveLinkIndex] = useState<number>(-1);

  const getContainerLinks = useCallback((): HTMLAnchorElement[] => {
    const container = containerRef.current;
    if (!container) return [];
    return Array.from(container.querySelectorAll('a:not(.ql-preview)'));
  }, []);

  // Memoize the link activation logic to prevent unnecessary re-creations
  // This function is called on both click and key press.
  const setLinkPreview = useCallback((link: HTMLAnchorElement, index?: number) => {
    const parsedData = parseLinkData(link.href);
    if (!parsedData) return;
    dispatch(AppUIActions.setPreviewPage(null));
    dispatch(AppUIActions.setReferencePage(null));

    if ( !index ){
      const links = getContainerLinks();
      const activeIdx = links.indexOf(link);
      index = activeIdx !== -1 ? activeIdx : 0;
    }

    setTimeout(() => {
      dispatch(AppUIActions.setPreviewPage({ ...parsedData, linkIndex: index, sx: previewPageSx }));
      dispatch(AppUIActions.setReferencePage({ ...parsedData, linkIndex: index, sx: previewPageSx }));
      dispatch(AppUIActions.setPreviewPageOpen(true));
    }, 0);
  }, [dispatch, previewPageSx, getContainerLinks]);

  const clearLinkPreview = useCallback(() => {
    dispatch(AppUIActions.setPreviewPageOpen(false));
    dispatch(AppUIActions.setPreviewPage(null));
    dispatch(AppUIActions.setReferencePage(null));
  }, [dispatch]);

  useEffect(() => {
    // This effect runs whenever the rawHtml or the containerRef changes.
    // It sets up the click and keyboard event listeners.
    const container = containerRef.current;
    if (!container) {
      return;
    }

    // This is the click handler for all links using event delegation.
    const handleLinkClick = (event: MouseEvent) => {
      const targetLink = (event.target as HTMLElement).closest('a');
      if (targetLink && targetLink.classList.contains('ql-preview')) return;
      if (targetLink && container.contains(targetLink)) {
        // Only intercept internal links
        const parsedData = parseLinkData(targetLink.href);
        if (!parsedData) {
          // External link, let browser handle
          return;
        }
        event.preventDefault();
        // Get all current links
        const links = getContainerLinks();
        const linkIndex = links.indexOf(targetLink as HTMLAnchorElement);
        setActiveLinkIndex(linkIndex);
        if (!disableLinkPreview) setLinkPreview(targetLink as HTMLAnchorElement, linkIndex);
      }
    };

    // Keyboard navigation handler.
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!container.contains(document.activeElement)) return;
      // Get all current links
      const links = getContainerLinks();
      const currentActiveLink = links[activeLinkIndex];
      if (currentActiveLink) currentActiveLink.classList.remove(activeLinkClassName);
      if (links.length === 0 || !previewPage) {
        setActiveLinkIndex(-1);
        return;
      }
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        let newIndex = activeLinkIndex;
        if (event.key === 'ArrowRight') {
          newIndex = (activeLinkIndex + 1) % links.length;
        } else if (event.key === 'ArrowLeft') {
          newIndex = (activeLinkIndex - 1 + links.length) % links.length;
        }
        setActiveLinkIndex(newIndex);
        links[newIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        setLinkPreview(links[newIndex], newIndex);
      } else if (event.key === 'Enter' && activeLinkIndex !== -1) {
        event.preventDefault();
        setLinkPreview(links[activeLinkIndex], activeLinkIndex);
      }
    };

    container.addEventListener('click', handleLinkClick);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      container.removeEventListener('click', handleLinkClick);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeLinkClassName, activeLinkIndex, setLinkPreview, disableLinkPreview, getContainerLinks, previewPage]);

  useEffect(() => {
    const container = containerRef.current;

    if (!container) return;

    const links = getContainerLinks();
    links.forEach(link => link.removeAttribute('class'));

    if (!previewPage) {
      setActiveLinkIndex(-1);
      return;
    }

    // Prefer to highlight the link matching referencePage if it exists, otherwise use previewPage
    const pageToMatch = referencePage || previewPage;

    // Find the link that matches the pageToMatch data
    const activeIdx = links.findIndex((link, idx) => {
      const parsedData = parseLinkData(link.href);
      if (!parsedData) return false;
      return (
        parsedData.documentId === pageToMatch?.documentId &&
        parsedData.pageNum === pageToMatch?.pageNum &&
        parsedData.bb === pageToMatch?.bb &&
        (pageToMatch?.linkIndex === undefined || pageToMatch?.linkIndex === idx)
      );
    });

    if (activeIdx !== -1) {
      links[activeIdx].classList.add('highlighted-link');
      setActiveLinkIndex(activeIdx);
    } else {
      setActiveLinkIndex(-1);
    }
  }, [previewPage, referencePage, getContainerLinks]);

  return { containerRef, setLinkPreview, clearLinkPreview };
}

function parseLinkData(href: string) {
  try {
    const url = new URL(href, window.location.origin);
    const regex = /^\/admin\/cases\/(.*?)\/documents\/(.*?)\/(\d+)$/;
    const pathMatch = url.pathname.match(regex);
    const bb = url.searchParams.get('bb') || undefined;
    if (!pathMatch) return null;
    const [, , documentId, pageNum] = pathMatch;
    return {
      documentId: +documentId,
      pageNum: +pageNum,
      bb
    };
  } catch {
    return null;
  }
}